import React, { FC, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import withRouter from "components/Common/withRouter";
import {
  getData as onGetData,
  updateData as onUpdateData,
  clearData
} from "store/AppMaster/actions";
import api from "constants/api";
import { RootState } from 'store/reducers';
import Label from "components/Label/Label";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import {
  Typography,
} from "@material-tailwind/react";

import Swal from 'sweetalert2';

export interface EditCustomerProps {
  data: any;
  result: any[]
}

const Edit: FC<EditCustomerProps> = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isApiCall, setIsApiCall] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [customerData, setCustomerData] = useState({
    id: "",
    name: "",
    email: "",
    mobile: "",
    designation: "",
    company_name: "",
    address: "",
    state: "",
    country: "",
    pincode: "",
    gstin: "",
    city: "",
  });

  useEffect(() => {
    if (isApiCall == 0) {
      getCustomerDetails();
      setIsApiCall(1);
    }
  }, [isApiCall]);

  const { data } = useSelector((state: RootState) => state.AppMaster);

  const getCustomerDetails = () => {
    const filterData = {
      id: params.id
    };
    dispatch(onGetData(filterData, '', api.customerDetail));
  }

  useEffect(() => {
    if (data && data.customer) {
      setCustomerData({
        id: data.customer._id,
        name: data.customer.first_name + " " + data.customer.last_name || '',
        email: data.customer.email || '',
        mobile: data.customer.mobile || '',
        designation: data.customer.designation || '',
        company_name: data.customer.company_name || '',
        address: data.customer.company_address || '',
        state: data.customer.state || '',
        country: data.customer.country || '',
        pincode: data.customer.pincode || '',
        gstin: data.customer.gstin || '',
        city: data.customer.city || '',
      });
    }
  }, [data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCustomerData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!customerData.name || !customerData.email || !customerData.mobile) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Name, Email, and Mobile are required fields.',
      });
      return;
    }

    setIsLoading(true);
    dispatch(onUpdateData(customerData, '', api.updateCustomer));
  };

  useEffect(() => {
    if (data && data.updateCustomer) {
      setIsLoading(false);
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Customer updated successfully!',
        timer: 2000,
        showConfirmButton: false
      }).then(() => {
        navigate(`/admin/customer/${customerData.id}`);
      });
    }
  }, [data]);

  useEffect(() => {
    if (data && data.error) {
      setIsLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: data.error || 'Failed to update customer. Please try again.',
      });
    }
  }, [data]);

  return (
    <>
      <div className="container py-16 lg:pb-28 lg:pt-20">
        <Helmet>
          <title>Edit Customer | Vijaydeep</title>
        </Helmet>
        <div className="mb-12 sm:mb-16">
          <h2 className="block text-2xl sm:text-3xl lg:text-4xl font-semibold ">Edit Customer</h2>
        </div>

        <div className="listingSectionSidebar__wrap shadow-lg border-0 p-8 rounded-2xl mb-8 sm:mb-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <label className="block">
                <Label>Name *</Label>
                <Input
                  name="name"
                  value={customerData.name}
                  onChange={handleInputChange}
                  placeholder="Enter customer name"
                  className="mt-1"
                  required
                />
              </label>

              <label className="block">
                <Label>Email *</Label>
                <Input
                  name="email"
                  type="email"
                  value={customerData.email}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  className="mt-1"
                  required
                />
              </label>

              <label className="block">
                <Label>Mobile *</Label>
                <Input
                  name="mobile"
                  value={customerData.mobile}
                  onChange={handleInputChange}
                  placeholder="Enter mobile number"
                  className="mt-1"
                  required
                />
              </label>

              <label className="block">
                <Label>Designation</Label>
                <Input
                  name="designation"
                  value={customerData.designation}
                  onChange={handleInputChange}
                  placeholder="Enter designation"
                  className="mt-1"
                />
              </label>

              <label className="block">
                <Label>Company Name</Label>
                <Input
                  name="company_name"
                  value={customerData.company_name}
                  onChange={handleInputChange}
                  placeholder="Enter company name"
                  className="mt-1"
                />
              </label>

              <label className="block">
                <Label>Pincode</Label>
                <Input
                  name="pincode"
                  value={customerData.pincode}
                  onChange={handleInputChange}
                  placeholder="Enter pincode"
                  className="mt-1"
                />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <label className="block">
                <Label>GSTIN</Label>
                <Input
                  name="gstin"
                  value={customerData.gstin}
                  onChange={handleInputChange}
                  placeholder="Enter GSTIN"
                  className="mt-1"
                />
              </label>

              <label className="block">
                <Label>State</Label>
                <Input
                  name="state"
                  value={customerData.state}
                  onChange={handleInputChange}
                  placeholder="Enter state"
                  className="mt-1"
                />
              </label>

              <label className="block">
                <Label>Country</Label>
                <Input
                  name="country"
                  value={customerData.country}
                  onChange={handleInputChange}
                  placeholder="Enter country"
                  className="mt-1"
                />
              </label>
            </div>

            <label className="block">
              <Label>Address</Label>
              <Textarea
                name="address"
                value={customerData.address}
                onChange={handleInputChange}
                placeholder="Enter address"
                className="mt-1"
                rows={3}
              />
            </label>

            <div className="flex justify-end space-x-4 pt-6">
              <Link to={`/admin/customer/${customerData.id}`}>
                <button
                  type="button"
                  className="nc-Button relative h-auto inline-flex items-center justify-center rounded-full transition-colors text-sm font-medium py-3 px-4 sm:py-3.5 sm:px-6 text-neutral-700 dark:text-neutral-200 disabled:cursor-not-allowed bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
              </Link>

              <ButtonPrimary
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  'Update Customer'
                )}
              </ButtonPrimary>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default withRouter(Edit);
